import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SlotOpsTimeRestriction, User } from '../Modules/User/Model/User';
import { isSlotOpsOverrideActive } from './slotOpsOverride';

dayjs.extend(utc);
dayjs.extend(timezone);

const WEEKEND_CUTOFF_MINUTES = 12 * 60; // 12:00
const DEFAULT_WEEKDAY_CUTOFF_MINUTES = 17 * 60; // 17:00
const LIMITED_ADMIN_WEEKDAY_CUTOFF_MINUTES = 18 * 60; // 18:00
const LIMITED_ADMIN_WEEKEND_CUTOFF_MINUTES = 15 * 60; // 15:00
/** 제한형 관리자 중 평일 17:30(KST) 이후 제한 적용 계정 (주말은 15:00 통일) */
const LIMITED_ADMIN_EARLY_CUTOFF_USERNAMES = new Set(['fixup1', 'hs1234']);
const LIMITED_ADMIN_EARLY_CUTOFF_WEEKDAY_MINUTES = 17 * 60 + 30;
const SPECIAL_WEEKDAY_CUTOFF_MINUTES = 12 * 60; // 12:00
const SPECIAL_WEEKDAY_CUTOFF_START = dayjs.tz('2026-02-14', 'Asia/Seoul');
const SPECIAL_WEEKDAY_CUTOFF_END = dayjs.tz('2026-02-18', 'Asia/Seoul');
const FULL_RESTRICTION_DATE = '2026-03-02';

/** 2026-05-04 제외. 슬롯 작업 허용 00:00~13:00(KST), 13시 이후 제한(일반 관리자 제외). */
const MAY_2026_HALF_DAY_OPERATION_DATES = new Set([
  '2026-05-01',
  '2026-05-02',
  '2026-05-03',
  '2026-05-05',
]);
const MAY_2026_OPERATION_CUTOFF_MINUTES = 13 * 60;
const TODAY_EDIT_EXTENSION_DATE = '2026-05-11';
const TODAY_EDIT_EXTENSION_USERNAME = 'tkwkd';
const TODAY_EDIT_EXTENSION_CUTOFF_MINUTES = 19 * 60; // 19:00

/** 주말과 동일하게 12:00(KST) 이후 제한 (일반 관리자 제외, 제한형 관리자 포함) */
const WEEKEND_LIKE_OPERATION_DATES = new Set([
  '2026-05-25',
  '2026-06-03',
  '2026-07-17',
]);

const isMay2026HalfDayOperationDate = (nowKST: Dayjs): boolean =>
  MAY_2026_HALF_DAY_OPERATION_DATES.has(nowKST.format('YYYY-MM-DD'));

const isWeekendLikeOperationDate = (nowKST: Dayjs): boolean =>
  WEEKEND_LIKE_OPERATION_DATES.has(nowKST.format('YYYY-MM-DD'));

const isTodayEditExtension = (
  nowKST: Dayjs,
  user: Pick<User, 'grade' | 'username' | 'is_limited_partner_admin'>,
  action?: SlotRestrictionAction,
): boolean =>
  action === '수정' &&
  user.username === TODAY_EDIT_EXTENSION_USERNAME &&
  nowKST.format('YYYY-MM-DD') === TODAY_EDIT_EXTENSION_DATE;

type SlotRestrictionTarget =
  | Pick<User, 'grade' | 'username' | 'is_limited_partner_admin' | 'slot_ops_override' | 'slot_ops_time_restriction'>
  | User['grade'];
type SlotRestrictionAction = '수정' | '생성' | '삭제' | '연장' | '환불' | '발주';

const getServerPolicy = (
  target: SlotRestrictionTarget,
): SlotOpsTimeRestriction | null =>
  typeof target === 'string' ? null : target.slot_ops_time_restriction ?? null;

const isRestrictedByServerPolicy = (
  policy: SlotOpsTimeRestriction,
  nowKST: Dayjs,
): boolean => {
  if (!policy.applies) return false;
  const currentMinutes = nowKST.hour() * 60 + nowKST.minute();
  const today = nowKST.format('YYYY-MM-DD');
  if (policy.today_date === today && policy.today_cutoff_minutes != null) {
    return currentMinutes >= policy.today_cutoff_minutes;
  }
  const serverDay = (nowKST.day() + 6) % 7; // Python weekday: 월=0 ... 일=6
  if (policy.daily_cutoff_minutes?.length === 7) {
    return currentMinutes >= policy.daily_cutoff_minutes[serverDay];
  }
  const isWeekend = nowKST.day() === 0 || nowKST.day() === 6;
  const cutoff = isWeekend
    ? policy.weekend_cutoff_minutes ?? WEEKEND_CUTOFF_MINUTES
    : policy.weekday_cutoff_minutes ?? DEFAULT_WEEKDAY_CUTOFF_MINUTES;
  return currentMinutes >= cutoff;
};

const formatCutoffLabel = (minutes?: number, fallback?: string): string => {
  if (minutes == null || Number.isNaN(minutes)) return fallback || '--:--';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const isWithinSpecialWeekdayCutoffRange = (nowKST: Dayjs): boolean => {
  const isOnOrAfterStart =
    nowKST.isAfter(SPECIAL_WEEKDAY_CUTOFF_START, 'day') ||
    nowKST.isSame(SPECIAL_WEEKDAY_CUTOFF_START, 'day');
  const isOnOrBeforeEnd =
    nowKST.isBefore(SPECIAL_WEEKDAY_CUTOFF_END, 'day') ||
    nowKST.isSame(SPECIAL_WEEKDAY_CUTOFF_END, 'day');

  return isOnOrAfterStart && isOnOrBeforeEnd;
};

const getWeekdayCutoffMinutes = (nowKST: Dayjs): number =>
  isWithinSpecialWeekdayCutoffRange(nowKST)
    ? SPECIAL_WEEKDAY_CUTOFF_MINUTES
    : DEFAULT_WEEKDAY_CUTOFF_MINUTES;

const normalizeRestrictionTarget = (
  target: SlotRestrictionTarget,
): Pick<User, 'grade' | 'username' | 'is_limited_partner_admin'> =>
  typeof target === 'string'
    ? { grade: target, username: '', is_limited_partner_admin: false }
    : target;

const isLimitedAdminEarlyCutoff = (
  user: Pick<User, 'grade' | 'username' | 'is_limited_partner_admin'>,
): boolean =>
  user.grade === 'ADMIN' &&
  Boolean(user.is_limited_partner_admin) &&
  LIMITED_ADMIN_EARLY_CUTOFF_USERNAMES.has(user.username?.toLowerCase() ?? '');

/**
 * 현재 시간이 슬롯 발주/환불(및 기타 작업)이 제한되는 시간인지 확인
 * - 일반 ADMIN(비제한형) 은 항상 허용
 * - 2026-05-01, 02, 03, 05(KST)만 슬롯 작업 허용 00:00~13:00, 5/4 및 그 외 날은 기존 규칙
 * - 제한형 ADMIN 은 평일 18:00(KST) 이후, 주말 15:00(KST) 이후 제한 (fixup1·hs1234: 평일 17:30)
 * - 2026-03-02(KST) 당일에는 관리자 외 전면 제한
 * - 주말(토/일)은 12:00(KST) 이후 제한 (12시 전에는 허용)
 * - 평일은 17:00(KST) 이후 제한
 * - 2026-02-14 ~ 2026-02-18 기간의 평일은 12:00(KST) 이후 제한
 * - 2026-05-25, 2026-06-03, 2026-07-17(KST) 당일은 주말과 동일하게 12:00(KST) 이후 제한 (일반 관리자 제외)
 */
export function isSlotOperationRestricted(
  target: SlotRestrictionTarget,
  action?: SlotRestrictionAction,
): boolean {
  const user = normalizeRestrictionTarget(target);

  if (typeof target !== 'string' && isSlotOpsOverrideActive(target)) {
    return false;
  }

  const serverPolicy = getServerPolicy(target);
  if (serverPolicy) {
    return isRestrictedByServerPolicy(serverPolicy, dayjs().tz('Asia/Seoul'));
  }

  const isLimitedAdmin = user.grade === 'ADMIN' && Boolean(user.is_limited_partner_admin);

  if (user.grade === 'ADMIN' && !isLimitedAdmin) {
    return false;
  }

  const nowKST = dayjs().tz('Asia/Seoul');

  // 2026-03-02(KST) 당일에는 전면 제한
  if (nowKST.format('YYYY-MM-DD') === FULL_RESTRICTION_DATE) {
    return true;
  }

  const currentMinutes = nowKST.hour() * 60 + nowKST.minute();

  if (isTodayEditExtension(nowKST, user, action)) {
    return currentMinutes >= TODAY_EDIT_EXTENSION_CUTOFF_MINUTES;
  }

  // 2026-05/1, 2, 3, 5 (KST)만 슬롯 작업 허용 ~13시. 5/4 및 어드민(비제한형) 규칙은 위에서 처리됨.
  if (isMay2026HalfDayOperationDate(nowKST)) {
    return currentMinutes >= MAY_2026_OPERATION_CUTOFF_MINUTES;
  }

  // 특정 평일도 주말과 동일하게 12:00(KST) 이후 제한 (일반 관리자 제외, 제한형 관리자 포함)
  if (isWeekendLikeOperationDate(nowKST)) {
    return currentMinutes >= WEEKEND_CUTOFF_MINUTES;
  }

  const dayOfWeek = nowKST.day(); // 0: 일요일, 6: 토요일
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  if (isWeekend) {
    if (isLimitedAdmin) {
      return currentMinutes >= LIMITED_ADMIN_WEEKEND_CUTOFF_MINUTES;
    }
    return currentMinutes >= WEEKEND_CUTOFF_MINUTES;
  }

  if (isLimitedAdmin) {
    const weekdayCutoff = isLimitedAdminEarlyCutoff(user)
      ? LIMITED_ADMIN_EARLY_CUTOFF_WEEKDAY_MINUTES
      : LIMITED_ADMIN_WEEKDAY_CUTOFF_MINUTES;
    return currentMinutes >= weekdayCutoff;
  }

  const cutoffMinutes = getWeekdayCutoffMinutes(nowKST);

  return currentMinutes >= cutoffMinutes;
}

/**
 * 제한 시간에 대한 설명 메시지 반환
 */
export function getRestrictionMessage(
  action: SlotRestrictionAction = '수정',
  target?: SlotRestrictionTarget,
): string {
  const nowKST = dayjs().tz('Asia/Seoul');
  const user = target ? normalizeRestrictionTarget(target) : null;
  const serverPolicy = target ? getServerPolicy(target) : null;
  if (serverPolicy) {
    if (!serverPolicy.applies) {
      return `관리자는 슬롯 ${action} 시간 제한이 없습니다.`;
    }
    if (serverPolicy.message) {
      return serverPolicy.message.replace(/작업/g, action);
    }
    return `한국 시간 기준 평일 ${formatCutoffLabel(serverPolicy.weekday_cutoff_minutes, serverPolicy.weekday_cutoff)} 이후 또는 주말 ${formatCutoffLabel(serverPolicy.weekend_cutoff_minutes, serverPolicy.weekend_cutoff)} 이후에는 슬롯 ${action}이 제한됩니다.`;
  }
  const isLimitedAdmin =
    user?.grade === 'ADMIN' && Boolean(user.is_limited_partner_admin);

  if (nowKST.format('YYYY-MM-DD') === FULL_RESTRICTION_DATE) {
    if (isLimitedAdmin) {
      return `2026년 3월 2일(KST)에는 제한형 관리자의 슬롯 ${action}이 제한됩니다.`;
    }
    return `2026년 3월 2일(KST)에는 관리자 외 슬롯 ${action}이 제한됩니다.`;
  }

  if (isMay2026HalfDayOperationDate(nowKST)) {
    return `2026년 5월 1·2·3·5일(KST)에는 13:00까지 슬롯 ${action}이 가능하며, 13:00 이후에는 제한됩니다. (일반 관리자 제외)`;
  }

  if (isWeekendLikeOperationDate(nowKST)) {
    return `${nowKST.format('YYYY년 M월 D일')}(KST)에는 주말과 동일하게 12:00까지 슬롯 ${action}이 가능하며, 12:00 이후에는 제한됩니다. (일반 관리자 제외)`;
  }

  if (user && isTodayEditExtension(nowKST, user, action)) {
    return `2026년 5월 11일(KST)에는 tkwkd 계정만 19:00까지 슬롯 수정이 가능하며, 19:00 이후에는 제한됩니다. (일반 관리자 제외)`;
  }

  if (isLimitedAdmin) {
    if (user && isLimitedAdminEarlyCutoff(user)) {
      return `한국 시간 기준 제한형 관리자(${user.username})는 평일 17:30 이후 또는 주말 15:00 이후 슬롯 ${action}이 제한됩니다.`;
    }
    return `한국 시간 기준 제한형 관리자는 평일 18:00 이후 또는 주말 15:00 이후 슬롯 ${action}이 제한됩니다.`;
  }

  const weekdayCutoffMinutes = getWeekdayCutoffMinutes(nowKST);
  const weekdayCutoffHour = Math.floor(weekdayCutoffMinutes / 60);

  return `한국 시간 기준 평일 ${weekdayCutoffHour}:00 이후 또는 주말 12:00 이후에는 관리자 외 슬롯 ${action}이 제한됩니다.`;
}
